/**
 * finalizeOutcome — ce que dit un refus de `POST api/activities/{id}/finalize`.
 *
 * Le serveur (`Api\ActivityController::finalizeActivity`) refuse en 403 dans deux cas qui n'ont rien
 * à voir, et ne les distingue que par sa PHRASE : « L'activité a déjà été soumise. » (une copie déjà
 * rendue : pour l'élève, c'est un succès — un renvoi après un trou réseau, typiquement) et « Ton
 * travail / Ton vocal n'est pas encore sauvegardé. Attends un peu et réessaie. » (aucun journal de la
 * copie, et pas de clé à recoller). L'ancien écran lisait ces phrases à deux endroits
 * (`ActivityScreen.js`, `useFinalizeSilentlyIfHasMedia.js`) ; c'est ici la SEULE lecture du texte,
 * testée. Un code d'erreur côté serveur (`error: "already_submitted" | "media_not_saved"`) la
 * rendrait inutile : à proposer au backend, sans changer le tuteur (il ne finalise pas par ce point).
 *
 * Le reste se lit sur le statut : 404 (l'activité n'existe plus), hors ligne, 5xx (aujourd'hui muet
 * chez l'élève : `treatError` ne connaissait que le 404), tout autre refus.
 */

import { type ApiFailure } from "../shared/apiCall"

export type FinalizeFailureKind =
    /** 403 « déjà été soumise » : la copie est rendue, c'est un succès. */
    | "already_submitted"
    /** 403 « n'est pas encore sauvegardé » : le serveur n'a pas la copie. */
    | "not_saved"
    /** 404 : l'activité n'existe plus (le tuteur l'a retirée). */
    | "not_found"
    /** Pas de réseau : la requête n'est pas partie. */
    | "offline"
    /** 5xx, ou une réponse sans statut : le serveur a cassé. */
    | "server"
    /** Tout autre refus (4xx). */
    | "rejected"

const ALREADY_SUBMITTED = "déjà été soumise"
const NOT_SAVED = "n'est pas encore sauvegardé"

export function classifyFinalizeError(error: ApiFailure | null | undefined): FinalizeFailureKind {
    if (error?.isOffline || error?.status === 0) return "offline"
    const status = error?.status ?? null
    const message = typeof error?.message === "string" ? error.message : ""
    if (status === 403 && message.includes(ALREADY_SUBMITTED)) return "already_submitted"
    if (status === 403 && message.includes(NOT_SAVED)) return "not_saved"
    if (status === 404) return "not_found"
    if (status == null || status >= 500) return "server"
    return "rejected"
}
